import { css } from "@emotion/react";

export const appContainer = css`
  width: 100%;
  background: var(--container-bg);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  padding: 40px;
  box-shadow: 0 2px 8px var(--shadow-light);
  transition:
    background 0.3s ease,
    box-shadow 0.3s ease;

  @media (max-width: 768px) {
    padding: 24px;
    border-radius: 12px;
  }

  @media (max-width: 640px) {
    padding: 16px;
    border-radius: var(--radius-md);
  }
`;

export const appContainerFull = css`
  max-width: none;
  width: 100%;
`;

export const appHeader = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;

  & > div:first-of-type {
    text-align: center;
    flex: 1;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 20px;

    & > div:first-of-type {
      text-align: left;
    }
  }
`;

export const appHeaderImports = css`
  margin-bottom: 24px;
`;

export const appTitle = css`
  font-family: var(--font-heading);
  font-size: var(--text-2xl);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 8px;
  transition: color 0.3s ease;

  @media (max-width: 768px) {
    font-size: var(--text-xl);
  }

  @media (max-width: 640px) {
    font-size: var(--text-lg);
  }
`;

export const appSubtitle = css`
  font-size: var(--text-base);
  color: var(--text-secondary);
  font-weight: 400;
  transition: color 0.3s ease;

  @media (max-width: 640px) {
    font-size: var(--text-sm);
  }
`;

export const pageContent = css`
  max-width: 800px;
  margin: 0 auto;
  padding: 48px 24px;

  @media (max-width: 640px) {
    padding: 24px 12px;
  }
`;

export const pageSection = css`
  margin-bottom: 48px;

  @media (max-width: 640px) {
    margin-bottom: 32px;
  }
`;

export const pageSectionHeader = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

export const pageSectionTitle = css`
  font-family: var(--font-heading);
  font-size: var(--text-xl);
  font-weight: 800;
  margin: 0;
  color: var(--text-primary);

  @media (max-width: 640px) {
    font-size: var(--text-lg);
  }
`;

export const pageSectionSubtitle = css`
  font-family: var(--font-heading);
  font-size: var(--text-lg);
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--text-primary);

  @media (max-width: 640px) {
    font-size: var(--text-base);
  }
`;

export const pageEmpty = css`
  font-size: var(--text-base);
  opacity: 0.7;
  text-align: center;
  padding: 40px;
  color: var(--text-secondary);

  @media (max-width: 640px) {
    padding: 24px;
    font-size: var(--text-sm);
  }
`;

export const pageDescription = css`
  font-size: var(--text-base);
  color: var(--text-secondary);
  margin-bottom: 32px;
`;

export const eventsList = css`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const eventItem = css`
  position: relative;
  background: var(--container-bg);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 8px var(--shadow-light);
  overflow: hidden;
  transition:
    box-shadow 0.3s ease,
    transform 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--color-primary), var(--color-gold));
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px var(--shadow-medium);
  }
`;

export const eventItemClickable = css`
  cursor: pointer;
`;

export const eventContent = css`
  padding: 20px 24px;
  padding-top: 24px;
  transition: background 0.3s ease;

  @media (max-width: 640px) {
    padding: 16px;
    padding-top: 20px;
  }
`;

export const eventName = css`
  font-family: var(--font-heading);
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  transition: color 0.3s ease;

  @media (max-width: 640px) {
    font-size: var(--text-base);
  }
`;

export const eventDate = css`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin: 0;
  transition: color 0.3s ease;
`;

export const formGroup = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const formLabel = css`
  font-family: var(--font-heading);
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--color-navy);
  display: block;
`;

export const formInput = css`
  width: 100%;
  padding: 12px 16px;
  border-radius: var(--radius-md);
  border: 2px solid var(--input-border);
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--text-primary);
  background: var(--input-bg);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--input-border-focus);
    box-shadow: 0 0 0 3px var(--selected-word-bg);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const formError = css`
  padding: 12px;
  border-radius: var(--radius-sm);
  background: var(--error-bg);
  color: var(--error-text);
  font-size: var(--text-sm);
`;

export const loading = css`
  text-align: center;
  padding: 60px 20px;
  font-size: var(--text-lg);
  color: var(--text-secondary);
  transition: color 0.3s ease;
`;

export const error = css`
  color: var(--error-text);
  background: var(--error-bg);
  border-radius: var(--radius-lg);
  padding: 20px;
  text-align: center;
  font-size: var(--text-lg);
  transition:
    color 0.3s ease,
    background 0.3s ease;
`;

export const success = css`
  color: var(--success-text);
  background: var(--success-bg);
  border-radius: var(--radius-lg);
  padding: 20px;
  text-align: center;
  font-size: var(--text-lg);
  transition:
    color 0.3s ease,
    background 0.3s ease;
`;

export const emptyState = css`
  text-align: center;
  padding: 60px 20px;
  color: var(--text-secondary);
  transition: color 0.3s ease;
`;

export const modalActions = css`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

export const modalDescription = css`
  margin-bottom: 12px;
  color: var(--text-secondary);
  font-size: 14px;
`;

export const actionButtons = css`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const searchResultsList = css`
  list-style: none;
  padding: 0;
  margin-top: 16px;
  max-height: 240px;
  overflow-y: auto;
`;

export const searchResultItem = css`
  border: 1px solid var(--input-border);
  border-radius: var(--radius-sm);
  padding: 12px 16px;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

export const searchResultInfo = css`
  flex: 1;
  min-width: 0;
`;

export const searchResultName = css`
  font-family: var(--font-heading);
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
`;

export const searchResultDate = css`
  font-size: var(--text-xs);
  color: var(--text-secondary);
  margin-top: 4px;
`;

export const searchHint = css`
  font-size: var(--text-xs);
  color: var(--text-secondary);
  margin-top: 4px;
`;

export const searchNoResults = css`
  font-size: 14px;
  color: var(--text-secondary);
  margin-top: 16px;
  text-align: center;
`;

export const searchResultAdded = css`
  font-family: var(--font-heading);
  font-size: var(--text-xs);
  font-weight: 700;
  color: var(--success-text);
  padding: 4px 10px;
  border-radius: var(--radius-full);
  background: var(--success-bg);
`;

export const fieldError = css`
  color: var(--error-text);
  font-size: var(--text-xs);
  margin-top: 4px;
`;

export const addedGridsSection = css`
  margin-top: 12px;
  margin-bottom: 4px;
`;

export const addedGridsLabel = css`
  font-family: var(--font-heading);
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-secondary);
  margin-bottom: 8px;
`;

export const addedGridsChips = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const addedGridChip = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: var(--radius-full);
  background: var(--success-bg);
  color: var(--success-text);
  font-family: var(--font-heading);
  font-size: var(--text-xs);
  font-weight: 700;
  border: 1px solid currentColor;
`;

export const invitationLinkRow = css`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const invitationLinkCode = css`
  font-size: var(--text-sm);
  word-break: break-all;
  flex: 1;
  min-width: 200px;
  color: var(--text-primary);

  @media (max-width: 640px) {
    font-size: var(--text-xs);
    min-width: 0;
  }
`;

// Table styles
export const tableContainer = css`
  overflow-x: auto;
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);

  @media (max-width: 768px) {
    border-radius: var(--radius-md);
  }
`;

export const table = css`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-sm);

  @media (max-width: 768px) {
    font-size: var(--text-xs);
  }
`;

export const tableHeader = css`
  background: var(--color-bg-warm);
  border-bottom: 2px solid var(--color-border);
`;

export const tableHeaderCell = css`
  padding: 12px 16px;
  text-align: left;
  font-family: var(--font-heading);
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: 10px 12px;
  }

  @media (max-width: 640px) {
    padding: 8px 10px;
  }
`;

export const tableRow = css`
  border-bottom: 1px solid var(--color-border);
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--color-bg-warm);
  }

  &:nth-of-type(even) {
    background: rgba(0, 0, 0, 0.02);

    &:hover {
      background: var(--color-bg-warm);
    }
  }
`;

export const tableCell = css`
  padding: 12px 16px;
  color: var(--text-primary);
  vertical-align: middle;

  @media (max-width: 768px) {
    padding: 10px 12px;
  }

  @media (max-width: 640px) {
    padding: 8px 10px;
  }
`;

export const tableCellActions = css`
  padding: 12px 16px;
  text-align: right;
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: 10px 12px;
  }

  @media (max-width: 640px) {
    padding: 8px 10px;
  }
`;

// Pagination styles
export const paginationContainer = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 12px;
  }
`;

export const paginationInfo = css`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  font-family: var(--font-heading);
  font-weight: 600;
`;

export const paginationButtons = css`
  display: flex;
  gap: 8px;
`;

// Mobile-friendly table (stacked cards on small screens)
export const mobileTableCard = css`
  display: none;

  @media (max-width: 640px) {
    display: block;
  }
`;

export const desktopTable = css`
  display: block;

  @media (max-width: 640px) {
    display: none;
  }
`;

export const mobileCard = css`
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 12px;
  margin-bottom: 8px;
  background: var(--container-bg);

  &:last-child {
    margin-bottom: 0;
  }
`;

export const mobileCardRow = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const mobileCardLabel = css`
  font-family: var(--font-heading);
  font-size: var(--text-xs);
  font-weight: 700;
  color: var(--text-secondary);
  min-width: 80px;
`;

export const mobileCardValue = css`
  font-size: var(--text-sm);
  color: var(--text-primary);
  text-align: right;
  flex: 1;
`;
